'use client'

import { Sku, SkuPriceOptionsResponse } from '@/types/interfaces/sku'
import { VisitOptionVideoVisitIcon } from '@/components/Icons/VisitOptionVideoVisitIcon'
import { VisitOptionCard } from '@/components/VisitOptions/VisitOptionCard'
import { useUserContext } from '@/contexts/UserContext'

interface VideoVisitOptionProps {
    sku: Sku
    prices: SkuPriceOptionsResponse | null
    availability?: string | null
    availabilityLoading?: boolean
    theme?: 'default' | 'tight'
    onClick?: () => void
}

export const VideoVisitOption = ({ sku, prices, availability, availabilityLoading, theme = 'default', onClick }: VideoVisitOptionProps) => {
    const { user } = useUserContext()
    const priceMapping = prices?.sku_price_mapping?.find((mapping) => String(mapping.id) === String(sku.id))
    const rawPrices = priceMapping?.prices
    const pricesArray = Array.isArray(rawPrices) ? rawPrices : rawPrices ? [rawPrices] : []
    // Flatten all options arrays from each SkuPricesResponse in pricesArray
    const allOptions = pricesArray.flatMap((price) => price.options || []) ?? []
    // Find the SkuPriceOption where type === 'video'
    const videoPriceOption = allOptions.find((opt) => opt.type === 'video')

    const insurancePrice = videoPriceOption?.insurance != null ? `$${videoPriceOption.insurance / 100}` : undefined
    const cashPrice = videoPriceOption?.cash != null ? `$${videoPriceOption.cash / 100}` : undefined

    let preferredPrice = null
    let preferredPriceType = null
    if (insurancePrice && cashPrice) {
        preferredPrice =
            parseFloat(insurancePrice.slice(1)) < parseFloat(cashPrice.slice(1)) ? insurancePrice : cashPrice
        preferredPriceType = parseFloat(insurancePrice.slice(1)) < parseFloat(cashPrice.slice(1)) ? 'insurance' : 'cash'
    } else if (insurancePrice) {
        preferredPrice = insurancePrice
        preferredPriceType = 'insurance'
    } else if (cashPrice) {
        preferredPrice = cashPrice
        preferredPriceType = 'cash'
    }

    let insuranceText = ''
    if (preferredPriceType === 'insurance' && !!user) {
        insuranceText = 'Your est. insurance price'
    } else if (preferredPriceType === 'insurance' && !user) {
        insuranceText = 'Typical copay'
    } else if (preferredPriceType === 'cash') {
        insuranceText = 'Without insurance'
    }

    let secondaryPriceText = ''
    if (preferredPriceType === 'insurance' && cashPrice) {
        secondaryPriceText = `or ${cashPrice} without insurance`
    } else if (preferredPriceType === 'cash' && insurancePrice) {
        secondaryPriceText = `or ${insurancePrice} with insurance`
    }

    return (
        <VisitOptionCard
            skuId={sku.id}
            optionType="video"
            key="video"
            icon={<VisitOptionVideoVisitIcon size={18} />}
            title="Video"
            price={preferredPrice ?? ''}
            insuranceText={theme === 'tight' ? (preferredPriceType === 'insurance' ? 'Est.' : 'w/o insurance') : insuranceText ?? ''}
            secondaryPrice={
                theme === 'tight'
                    ? preferredPriceType === 'insurance' && cashPrice
                        ? `or ${cashPrice} w/o insurance`
                        : undefined
                    : secondaryPriceText
            }
            availability={availability}
            availabilityLoading={availabilityLoading}
            theme={theme}
            onClick={onClick}
        />
    )
}
